import type { TimeEntry, Project, ISODateString } from '../types/timesheet.types';
import { ProjectType } from '../types/timesheet.types';
import { VALIDATION, BANK_HOLIDAY_PROJECT_CODE } from './constants';

type ProjectMap = ReadonlyMap<string, Pick<Project, 'type'>>;

export function getDailyTotal(entries: readonly TimeEntry[], date: ISODateString): number {
  return entries
    .filter(e => e.dateWorked === date)
    .reduce((sum, e) => sum + e.hours, 0);
}

export function getMonthlyTotal(entries: readonly TimeEntry[]): number {
  return entries.reduce((sum, e) => sum + e.hours, 0);
}

export function getHoursByProjectType(
  entries: readonly TimeEntry[],
  projectMap: ProjectMap
): Record<ProjectType, number> {
  const result: Record<ProjectType, number> = {
    [ProjectType.RD]: 0,
    [ProjectType.GA]: 0,
    [ProjectType.Leave]: 0,
    [ProjectType.Commercial]: 0,
    [ProjectType.Standard]: 0,
  };

  for (const entry of entries) {
    const project = projectMap.get(entry.projectCode);
    if (project) result[project.type] += entry.hours;
  }

  return result;
}

export function getTypePercentages(
  breakdown: Record<ProjectType, number>
): Record<ProjectType, number> {
  const total = Object.values(breakdown).reduce((s, h) => s + h, 0);
  if (total === 0) {
    return { [ProjectType.RD]: 0, [ProjectType.GA]: 0, [ProjectType.Leave]: 0, [ProjectType.Commercial]: 0, [ProjectType.Standard]: 0 };
  }
  return {
    [ProjectType.RD]: Math.round((breakdown[ProjectType.RD] / total) * 100),
    [ProjectType.GA]: Math.round((breakdown[ProjectType.GA] / total) * 100),
    [ProjectType.Leave]: Math.round((breakdown[ProjectType.Leave] / total) * 100),
    [ProjectType.Commercial]: Math.round((breakdown[ProjectType.Commercial] / total) * 100),
    [ProjectType.Standard]: Math.round((breakdown[ProjectType.Standard] / total) * 100),
  };
}

/** True when daily total exceeds the OOM warning threshold. */
export function isDailyOverThreshold(entries: readonly TimeEntry[], date: ISODateString): boolean {
  return getDailyTotal(entries, date) > VALIDATION.OOM_WARNING_THRESHOLD;
}

/** Rounds a decimal-hours value to the nearest 15-minute increment. */
export function roundToQuarterHour(value: number): number {
  return Math.round(value * 4) / 4;
}

/**
 * Parse a time string to decimal hours.
 * Accepts "2h 43m", "2h", "2.5" (=2h 30m), "2.25" (=2h 15m), "2" formats.
 * Decimal inputs are treated as fractional hours (not clock minutes).
 */
export function hhmmToDecimal(raw: string): number {
  if (!raw || !raw.trim()) return NaN;
  const s = raw.trim();
  const hmMatch = s.match(/^(\d+)h\s*(\d+)m?$/);
  if (hmMatch) {
    const h = parseInt(hmMatch[1], 10);
    const m = parseInt(hmMatch[2], 10);
    if (!isNaN(h) && !isNaN(m)) return h + m / 60;
  }
  const hOnlyMatch = s.match(/^(\d+)h$/);
  if (hOnlyMatch) {
    const h = parseInt(hOnlyMatch[1], 10);
    if (!isNaN(h)) return h;
  }
  const val = parseFloat(s);
  return isNaN(val) ? NaN : val;
}

/**
 * Convert decimal hours to a display string.
 * 2.25 → "2h 15m"  |  2.5 → "2h 30m"  |  7.0 → "7h"
 */
export function decimalToHHMM(decimal: number): string {
  if (!isFinite(decimal) || decimal < 0) return '';
  const h = Math.floor(decimal);
  const m = Math.round((decimal - h) * 60);
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

/** Formats an hours value as "Xh Ym". */
export function fmtHours(n: number): string {
  return decimalToHHMM(n);
}

/**
 * Builds a map of date → daily total for quick lookup.
 * Memoize this in the hook via useMemo.
 */
export function buildDailyTotalsMap(
  entries: readonly TimeEntry[]
): Map<ISODateString, number> {
  const map = new Map<ISODateString, number>();
  for (const entry of entries) {
    if (entry.projectCode === BANK_HOLIDAY_PROJECT_CODE) continue;
    map.set(entry.dateWorked, (map.get(entry.dateWorked) ?? 0) + entry.hours);
  }
  return map;
}
